import {
  CREATE_USER_REQUEST,
  CREATE_USER_SUCCESS,
  CREATE_USER_FAIL,
  LOGIN_USER_REQUEST,
  LOGIN_USER_SUCCESS,
  LOGIN_USER_FAIL,
  LOGIN_USER_RESET,
  GET_OTP_REQUEST,
  GET_OTP_SUCCESS,
  GET_OTP_FAIL,
  VERIFY_OTP,
  VERIFY_OTP_SUCCESS,
  VERIFY_OTP_FAIL,
  LOGOUT_USER_SUCCESS,
  LOGOUT_USER_REQUEST,
  LOGOUT_USER_FAIL,
} from "../constants/userConstants.js";

import UserService from "../../services/userService.js";
import TokenService from "../../services/tokenService.js";
import fcmService from "../../services/fcmService.js";
import notificationApi from "../../services/notificationApi.js";
import { toast } from "react-toastify";
import { data } from "autoprefixer";


/**
 * Securely store user data to localStorage with token management
 * @param {Object} responseData - User data from API response
 */
const pushToLocalStorage = (responseData) => {
  try {
    const actualData = responseData.data || responseData;
    
    console.log('Storing user data:', actualData);
    if (actualData.token) {
      if (actualData.expires_in) {
        // If backend provides expiry info, use it
        TokenService.setToken(
          actualData.token, 
          actualData.refresh_token, 
          actualData.expires_in
        );
      } else {
        TokenService.setSanctumToken(actualData.token);
      }
    }

    const storageItems = {
      userInfo: actualData.patient,
      hospital: actualData.hospital?.hospital_id || actualData.hospital_id,
      hospital_name: actualData.hospital?.system_name || actualData.hospital_name,
      hospital_phone: actualData.hospital?.phone || actualData.hospital_phone,
      type: actualData.type,
      consultation_audio: actualData.consultation_audio,
      consultation_video: actualData.consultation_video,
    };

    // Store each item in localStorage (excluding sensitive token data)
    Object.entries(storageItems).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        localStorage.setItem(key, JSON.stringify(value));
      }
    });
    
    console.log('User data stored securely');
    
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    toast.error('Failed to save user data locally');
  }
};

/**
 * Securely clear user data from localStorage and token storage
 */
const removeFromLocalStorage = () => {
  try {
    // Clear tokens using secure token service
    TokenService.clearTokens();
    
    // Clear other user data
    const keysToRemove = [
      "userInfo",
      "hospital_name", 
      "hospital_phone",
      "type",
      "consultation_audio",
      "consultation_video",
    ];

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    console.log('localStorage and tokens cleared successfully');
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};


const createUserAction = (filenumber) => async (dispatch) => {
  try {
    dispatch({ type: CREATE_USER_REQUEST });

    const data = await UserService.getPatient(filenumber);

    dispatch({
      type: CREATE_USER_SUCCESS,
      payload: data,
    });

    toast.success('Patient information retrieved successfully');
    
  } catch (error) {
    console.error('Create user error:', error);
    
    const errorMessage = error.response?.data?.message || error.message || 'Failed to retrieve patient information';
    const todo = error.response?.data?.errors;
    
    dispatch({
      type: CREATE_USER_FAIL,
      payload: {
        message: errorMessage,
        todo: todo === 'verify_otp' ? 'verify_otp' : null,
      },
    });

    if (todo !== 'verify_otp') {
      toast.error(errorMessage);
    }
  }
};

const getOTp = (file_number, phone_number) => async (dispatch) => {
  try {
    dispatch({ type: GET_OTP_REQUEST });

    const data = await UserService.sendOTP(file_number, phone_number);

    dispatch({
      type: GET_OTP_SUCCESS,
      payload: data,
    });

    toast.success('OTP sent successfully to your phone');
    
  } catch (error) {
    console.error('Get OTP error:', error);
    
    const errorMessage = error.response?.data?.message || error.message || 'Failed to send OTP';
    
    dispatch({
      type: GET_OTP_FAIL,
      payload: errorMessage,
    });

    toast.error(errorMessage);
  }
};

const verifyOtp = (file_number, phone_number, otp) => async (dispatch) => {
  try {
    dispatch({ type: VERIFY_OTP });

    const data = await UserService.verifyOTP(file_number, phone_number, otp);

    dispatch({
      type: VERIFY_OTP_SUCCESS,
      payload: data,
    });

    toast.success('OTP verified successfully');
    
  } catch (error) {
    console.error('Verify OTP error:', error);
    
    const errorMessage = error.response?.data?.message || error.message || 'Failed to verify OTP';
    
    dispatch({
      type: VERIFY_OTP_FAIL,
      payload: errorMessage,
    });

    toast.error(errorMessage);
  }
};

const loginUserAction = (file_number, password, patient_id=null, qr_code=null) => async (dispatch) => {
  try {
    dispatch({ type: LOGIN_USER_REQUEST });

    const data = await UserService.login(file_number, password, patient_id, qr_code);

    dispatch({
      type: LOGIN_USER_SUCCESS,
      payload: data,
    });

    // Save user data to localStorage
    pushToLocalStorage(data);
  
    console.log('Login successful:', data);
    
    // Register FCM token after successful login
    try {
      if (fcmService.isSupported && Notification.permission === 'granted') {
        const fcmToken = await fcmService.getToken();
        if (fcmToken) {
          const deviceInfo = {
            device_type: 'web',
            device_name: navigator.userAgent.includes('Mobile') ? 'Mobile Browser' : 'Desktop Browser',
            app_version: '1.0.0',
            os_version: navigator.platform
          };
          
          await notificationApi.registerToken(fcmToken, deviceInfo);
          console.log('FCM token registered successfully after login');
        }
      }
    } catch (fcmError) {
      console.warn('Failed to register FCM token after login:', fcmError);
      // Don't fail the login process if FCM registration fails
    }
    
  } catch (error) {
    console.error('Login error:', error);
    
    const errorMessage = error.response?.data?.message || error.message || 'Login failed';

    const todo = error.response?.data?.errors;

    dispatch({
      type: LOGIN_USER_FAIL,
      payload: { 
        data: error?.response,
        message: errorMessage, 
        todo: todo ==='verify_otp'?'verify_otp' : null,
      },
    });

    if (todo !== 'verify_otp') {
      toast.error(errorMessage);
    }
  }
};


const logoutUserAction = () => async (dispatch) => {
  try {
    dispatch({ type: LOGOUT_USER_REQUEST });
    
    // Remove FCM token before logout
    try {
      if (fcmService.isSupported) {
        const fcmToken = await fcmService.getToken();
        if (fcmToken) {
          await notificationApi.removeToken(fcmToken);
          console.log('FCM token removed successfully before logout');
        }
      }
    } catch (fcmError) {
      console.warn('Failed to remove FCM token before logout:', fcmError);
      // Don't fail the logout process if FCM removal fails
    }
    
    const data = await UserService.logout();

    dispatch({
      type: LOGOUT_USER_SUCCESS,
      payload: data,
    });

    // Clear localStorage regardless of API response
    removeFromLocalStorage();
    
    // Reset login state to prevent auto-navigation on next login page visit
    dispatch({ type: LOGIN_USER_RESET });
    
    toast.success('Logged out successfully');
    
  } catch (error) {
    console.error('Logout error:', error);
    
    const errorMessage = error.response?.data?.message || error.message || 'Logout failed';
    
    dispatch({
      type: LOGOUT_USER_FAIL,
      payload: { message: errorMessage },
    });

    // Clear localStorage even if logout API fails
    removeFromLocalStorage();
    
    // Reset login state even if logout API fails
    dispatch({ type: LOGIN_USER_RESET });
    
    // Show error toast but still consider logout successful locally
    toast.warning('Logged out locally, but server logout failed');
  }
};


export { createUserAction, loginUserAction, getOTp, verifyOtp, logoutUserAction };
